import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, X, Search, LogOut, FileText, Settings, ChevronDown, Share2, MessageCircle, KeyRound } from 'lucide-react';
import axios from 'axios';
import branding from '../../config/branding';
import { useCategories, useRegions, UP_CATEGORY } from '../../lib/taxonomy';
import { useArticleHeader } from '../../lib/articleHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const { t, language, setLanguage, toggleLanguage, isHindi } = useLanguage();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [breakingNews, setBreakingNews] = useState(null);

  useEffect(() => {
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      axios.get(`${API}/public/breaking-news`)
        .then(res => setBreakingNews(res.data.text || null))
        .catch(() => {});
    };
    load();
    // Keep the ticker current on long-open pages; the importer updates it every run
    const timer = setInterval(load, 2 * 60_000);
    return () => clearInterval(timer);
  }, []);

  // The LIVE strip shows one breaking headline at a time and rotates through them
  const breakingItems = (breakingNews || '').split(/\s*[•|]\s*/).map((x) => x.trim()).filter(Boolean);
  const [breakingIndex, setBreakingIndex] = useState(0);
  useEffect(() => {
    setBreakingIndex(0);
    if (breakingItems.length < 2) return undefined;
    const timer = setInterval(() => setBreakingIndex((i) => (i + 1) % breakingItems.length), 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakingNews]);

  const { pathname } = useLocation();
  // Story pages get a slim one-row header instead of the front-page masthead
  const isArticle = pathname.startsWith('/article/');
  const story = useArticleHeader();

  // Once the masthead's section row scrolls away, pin a copy of it to the top (desktop)
  const navRef = useRef(null);
  const [navPinned, setNavPinned] = useState(false);
  useEffect(() => {
    const el = navRef.current;
    if (!el || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setNavPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isArticle]);

  // Close the sections drawer whenever the page changes
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  // Stop the page behind the drawer from scrolling
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setSearchOpen(false);
      setMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const { topLevel, childrenOf, pathFor, byId } = useCategories();
  const { regions } = useRegions();
  const categoryName = (cat) => (
    cat.id === 'cultural' ? 'UP की तारीफ़' : (isHindi ? cat.name_hi : cat.name)
  );

  // What a section's dropdown lists: mandals for Rajya/Sahar, subcategories elsewhere
  const sectionChildren = (catId) => (
    catId === UP_CATEGORY
      ? regions.divisions.map((d) => ({ id: d.id, name: d.name, name_hi: d.name_hi, to: `/rajya/${d.id}` }))
      : childrenOf(catId).map((c) => ({ ...c, to: pathFor(c.id) }))
  );

  // Highlight the section the reader is in (a subcategory highlights its parent, e.g. Other)
  const activeTopId = (() => {
    if (pathname.startsWith('/rajya')) return 'uttar-pradesh';
    const m = pathname.match(/^\/category\/([^/]+)/);
    if (!m) return null;
    return byId[m[1]]?.parent_id || m[1];
  })();

  const font = isHindi ? 'font-hindi' : '';
  const today = new Date().toLocaleDateString(isHindi ? 'hi-IN' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const masthead = (className) => (
    <Link to="/" className={`block text-[var(--ink)] leading-none ${className}`} data-testid="logo-link" aria-label={branding.name}>
      <span className={`font-masthead ${isHindi ? 'font-hindi' : ''}`}>
        {isHindi ? branding.nameHi : branding.name}
      </span>
    </Link>
  );

  const languageLabel = language === 'en' ? 'हिंदी' : 'English';

  // Left side of the utility row: sections drawer and search
  const menuAndSearch = (
    <div className="flex items-center gap-1">
      <button
        onClick={() => setMenuOpen(true)}
        className="flex items-center justify-center w-9 h-9 hover:bg-[var(--bg-subtle)]"
        aria-label={isHindi ? 'सभी सेक्शन' : 'Sections'}
        data-testid="sections-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </button>
      <button
        onClick={() => setSearchOpen((v) => !v)}
        className="flex items-center justify-center w-9 h-9 hover:bg-[var(--bg-subtle)]"
        aria-label={t('search')}
        aria-expanded={searchOpen}
      >
        <Search className="w-4 h-4" />
      </button>
      {searchOpen && (
        <form onSubmit={handleSearch} className="flex items-center gap-2 ml-1 animate-fade-in">
          <input
            type="text"
            autoFocus
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search')}
            className={`h-8 w-64 px-3 border border-[var(--rule)] text-sm focus:border-[var(--ink)] outline-none ${font}`}
            data-testid="search-input"
          />
          <button type="submit" className="h-8 px-3 bg-[var(--ink)] text-white font-ui text-[11px] font-bold uppercase tracking-wider">
            {isHindi ? 'खोजें' : 'Go'}
          </button>
        </form>
      )}
    </div>
  );

  // Sign up / log in, or the account menu once signed in
  const accountButtons = (
    <>
    {isAuthenticated ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1 font-ui text-xs font-bold uppercase tracking-wider text-[var(--ink)]" data-testid="user-menu-trigger">
            {user?.name?.split(' ')[0] || (isHindi ? 'खाता' : 'Account')}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-none">
          <DropdownMenuItem asChild>
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
              <FileText className="w-4 h-4" />
              {t('dashboard')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/account/password" className="flex items-center gap-2"><KeyRound className="w-4 h-4" />{isHindi ? 'पासवर्ड बदलें' : 'Change password'}</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/editor" className="flex items-center gap-2" data-testid="write-article-link">
              <FileText className="w-4 h-4" />
              {t('writeArticle')}
            </Link>
          </DropdownMenuItem>
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                  <Settings className="w-4 h-4" />
                  {t('admin')}
                </Link>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            {t('logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <>
        <Link
          to="/login"
          className="inline-flex items-center h-8 px-3 border border-[var(--ink)] text-[var(--ink)] font-ui text-[11px] font-bold uppercase tracking-wider hover:bg-[var(--bg-subtle)]"
          data-testid="login-btn"
        >
          {t('login')}
        </Link>
      </>
    )}
    </>
  );

  // Right side of the story-page header: language switch and account
  const accountControls = (
    <div className="flex items-center gap-3">
      <button
        onClick={toggleLanguage}
        className={`font-ui text-xs font-semibold text-[var(--ink)] hover:underline underline-offset-4 ${language === 'en' ? 'font-hindi' : ''}`}
        data-testid="language-toggle"
      >
        {languageLabel}
      </button>
      <span className="h-4 border-l border-[var(--rule)]" />
      {accountButtons}
    </div>
  );

  // Edition switch in the centre of the front-page utility row, like a paper's editions
  const editionLinks = (
    <div className="flex items-center gap-5 font-ui text-[11px] tracking-[0.12em] uppercase text-[var(--ink-soft)]">
      {[['en', 'English'], ['hi', 'हिंदी']].map(([code, name]) => (
        <button
          key={code}
          onClick={() => setLanguage(code)}
          aria-pressed={language === code}
          className={`hover:underline underline-offset-4 ${language === code ? 'font-bold text-[var(--ink)]' : ''} ${code === 'hi' ? 'font-hindi tracking-normal text-xs' : ''}`}
          data-testid={code === language ? undefined : 'language-toggle'}
        >
          {name}
        </button>
      ))}
    </div>
  );

  // Section links: one scrollable row on phones; on desktop each section with subsections
  // gets a hover dropdown (mandals under Rajya/Sahar)
  const sectionNav = (
    <div className="flex items-center md:justify-center md:gap-x-2 overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
      <Link
        to="/"
        className={`section-link nav-pill whitespace-nowrap shrink-0 ${pathname === '/' ? 'is-active' : ''} ${font}`}
        data-testid="category-home"
      >
        {isHindi ? 'होम' : 'Home'}
      </Link>
      {topLevel.map((cat) => {
        const kids = sectionChildren(cat.id);
        return (
          <div key={cat.id} className="relative group shrink-0">
            <Link
              to={pathFor(cat.id)}
              aria-current={activeTopId === cat.id ? 'page' : undefined}
              className={`section-link nav-pill whitespace-nowrap gap-1 ${activeTopId === cat.id ? 'is-active' : ''} ${font}`}
              data-testid={`category-${cat.id}`}
            >
              {categoryName(cat)}
              {kids.length > 0 && <ChevronDown className="hidden md:inline w-3.5 h-3.5 text-[var(--ink-muted)]" />}
            </Link>
            {kids.length > 0 && (
              <div className="hidden md:group-hover:block md:group-focus-within:block absolute left-1/2 -translate-x-1/2 top-full z-50 pt-1">
                <div className="bg-white border border-[var(--rule)] shadow-lg py-2 min-w-[220px] max-h-[60vh] overflow-y-auto text-left">
                  <Link
                    to={pathFor(cat.id)}
                    className={`block px-4 py-1.5 font-ui text-[13px] font-bold text-[var(--ink)] hover:bg-[var(--bg-subtle)] ${font}`}
                  >
                    {isHindi ? `सभी ${cat.name_hi}` : `All ${cat.name}`}
                  </Link>
                  {kids.map((k) => (
                    <Link
                      key={k.id}
                      to={k.to}
                      className={`block px-4 py-1.5 font-ui text-[13px] text-[var(--ink-soft)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)] ${font}`}
                    >
                      {isHindi ? k.name_hi : k.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Slim one-row header for story pages. Once the reader scrolls past the headline it
  // swaps to the story's section, headline and share buttons
  const shareIcon = 'flex items-center justify-center w-9 h-9 rounded-full border border-[var(--rule)] text-[var(--ink)] hover:border-[var(--ink)] transition-colors';
  const articleHeader = (
    <header className="safe-top bg-white sticky top-0 z-50 border-b border-[var(--rule)]">
      <div className="max-w-7xl mx-auto px-4">
        {story?.scrolled ? (
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 h-12 md:h-14 animate-fade-in">
            {masthead('text-[24px] md:text-[28px]')}
            <p className="min-w-0 truncate md:text-center">
              <Link to={story.sectionTo} className={`kicker text-[var(--ink)] hover:underline underline-offset-4 hidden sm:inline ${font}`}>
                {story.sectionLabel}
              </Link>
              <span className="hidden sm:inline mx-2 text-[var(--rule)]">|</span>
              <span className={`text-sm text-[var(--ink)] ${isHindi ? 'font-hindi' : 'font-heading'}`}>{story.title}</span>
            </p>
            <div className="flex items-center gap-2">
              <a href={story.whatsappHref} target="_blank" rel="noopener noreferrer" className={shareIcon} aria-label="WhatsApp">
                <MessageCircle className="w-4 h-4" />
              </a>
              <button type="button" onClick={story.onShare} className={shareIcon} aria-label={isHindi ? 'शेयर करें' : 'Share'}>
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-[1fr_auto_1fr] items-center h-12 md:h-14">
            <div className="flex items-center gap-2 min-w-0">
              <div className="hidden md:block">{menuAndSearch}</div>
              <button
                onClick={() => setMenuOpen(true)}
                className="md:hidden flex items-center justify-center w-11 h-11 -ml-2"
                aria-label={isHindi ? 'मेनू' : 'Menu'}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
              {story?.sectionLabel && !searchOpen && (
                <Link
                  to={story.sectionTo}
                  className={`hidden sm:inline font-ui text-[13px] font-bold uppercase tracking-wider text-[var(--ink)] hover:underline underline-offset-4 truncate ${font}`}
                >
                  {story.sectionLabel}
                </Link>
              )}
            </div>
            {masthead('text-[28px] md:text-[34px] text-center px-4')}
            <div className="flex justify-end">
              <div className="hidden md:block">{accountControls}</div>
              <button
                onClick={toggleLanguage}
                className={`md:hidden min-h-[44px] pl-2 font-ui text-xs font-semibold text-[var(--ink)] ${language === 'en' ? 'font-hindi' : ''}`}
                data-testid="language-toggle-mobile"
              >
                {languageLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );

  return (
    <>
    {isArticle ? articleHeader : (
    <header className="safe-top bg-white sticky top-0 z-50 md:static">
      <div className="max-w-7xl mx-auto px-4">

        {/* Utility row: sections + search on the left, editions in the middle, account on the right */}
        <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center h-12">
          {menuAndSearch}
          {editionLinks}
          <div className="flex items-center justify-end gap-3">{accountButtons}</div>
        </div>

        {/* Desktop masthead: date on the left, nameplate centred */}
        <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-end pb-4">
          <div className="pb-1">
            <p className={`font-ui text-xs font-bold text-[var(--ink)] ${font}`}>{today}</p>
            <p className={`font-ui text-xs text-[var(--ink-muted)] mt-0.5 ${font}`}>
              {isHindi ? branding.taglineHi : branding.tagline}
            </p>
          </div>
          {masthead('text-[64px] lg:text-[76px] text-center px-6')}
          <div />
        </div>

        {/* Phone header: menu, nameplate, language */}
        <div className="md:hidden grid grid-cols-[44px_1fr_auto] items-center h-14">
          <button
            onClick={() => setMenuOpen(true)}
            className="flex items-center justify-center w-11 h-11 -ml-2"
            aria-label={isHindi ? 'मेनू' : 'Menu'}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          {masthead('text-[34px] text-center')}
          <button
            onClick={toggleLanguage}
            className={`min-h-[44px] pl-2 font-ui text-xs font-semibold text-[var(--ink)] ${language === 'en' ? 'font-hindi' : ''}`}
            data-testid="language-toggle-mobile"
          >
            {languageLabel}
          </button>
        </div>

        {/* Section navigation, finished with a double rule like a printed front page */}
        <nav ref={navRef} className="double-rule border-t border-[var(--rule)] md:border-t-0">
          {sectionNav}
        </nav>
      </div>

      {/* LIVE strip: one breaking headline at a time, centred under the masthead */}
      {breakingItems.length > 0 && (
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-3 py-2.5 border-b border-[var(--rule)] min-w-0" aria-live="polite">
            <span className={`flex items-center gap-1.5 font-ui text-[11px] font-extrabold uppercase tracking-[0.1em] text-[var(--live)] shrink-0 ${font}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current live-badge" />
              {isHindi ? 'लाइव' : 'Live'}
            </span>
            <p
              key={breakingIndex}
              className={`min-w-0 truncate font-ui text-sm font-bold text-[var(--ink)] animate-fade-in ${font}`}
            >
              {breakingItems[breakingIndex % breakingItems.length]}
            </p>
            {breakingItems.length > 1 && (
              <span className="story-meta shrink-0 hidden sm:inline">
                {(breakingIndex % breakingItems.length) + 1}/{breakingItems.length}
              </span>
            )}
          </div>
        </div>
      )}
    </header>
    )}

      {/* Section row pinned to the top once the masthead has scrolled away (desktop) */}
      {!isArticle && navPinned && (
        <div className="hidden md:block fixed top-0 inset-x-0 z-50 bg-white border-b border-[var(--rule)] shadow-sm animate-fade-in">
          <div className="max-w-7xl mx-auto px-4">{sectionNav}</div>
        </div>
      )}

      {/* Sections drawer (desktop hamburger and phone menu) */}
      {menuOpen && (
        <div className="fixed inset-0 z-[100] flex" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={() => setMenuOpen(false)} />
          <div className="relative w-full sm:w-80 h-full bg-white overflow-y-auto p-5 safe-top shadow-xl animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              {masthead('text-3xl')}
              <button onClick={() => setMenuOpen(false)} className="flex items-center justify-center w-11 h-11 -mr-2" aria-label={isHindi ? 'बंद करें' : 'Close'}>
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="flex mb-6">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search')}
                className={`flex-1 min-w-0 h-11 px-3 border border-[var(--rule)] focus:border-[var(--ink)] outline-none ${font}`}
              />
              <button type="submit" className="h-11 px-4 bg-[var(--ink)] text-white" aria-label={t('search')}>
                <Search className="w-4 h-4" />
              </button>
            </form>

            {/* Sections */}
            <p className={`kicker text-[var(--ink-muted)] mb-2 ${font}`}>{isHindi ? 'सेक्शन' : 'Sections'}</p>
            <div className="border-t border-[var(--ink)] mb-6">
              {topLevel.map((cat) => (
                <div key={cat.id} className="border-b border-[var(--rule)]">
                  <Link
                    to={pathFor(cat.id)}
                    className={`block py-3 font-heading text-lg font-semibold text-[var(--ink)] hover:underline underline-offset-4 ${isHindi ? 'font-hindi-heading' : ''}`}
                  >
                    {categoryName(cat)}
                  </Link>
                  {childrenOf(cat.id).length > 0 && (
                    <div className="pb-3 -mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      {childrenOf(cat.id).map((sub) => (
                        <Link
                          key={sub.id}
                          to={pathFor(sub.id)}
                          className={`font-ui text-sm text-[var(--ink-soft)] hover:underline underline-offset-4 min-h-[32px] inline-flex items-center ${font}`}
                        >
                          {isHindi ? sub.name_hi : sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Account */}
            <p className={`kicker text-[var(--ink-muted)] mb-2 ${font}`}>{isHindi ? 'खाता' : 'Account'}</p>
            <div className="border-t border-[var(--ink)] pt-3">
              {isAuthenticated ? (
                <div className="font-ui">
                  <p className="font-semibold text-[var(--ink)]">{user?.name}</p>
                  <p className="text-sm text-[var(--ink-muted)] mb-3">{user?.email}</p>
                  <Link to="/dashboard" className="block py-2.5 text-sm hover:underline underline-offset-4">{t('dashboard')}</Link>
                  <Link to="/account/password" className="flex items-center gap-2 py-2.5 text-sm hover:underline underline-offset-4"><KeyRound className="w-4 h-4" />{isHindi ? 'पासवर्ड बदलें' : 'Change password'}</Link>
                  <Link to="/editor" className="block py-2.5 text-sm hover:underline underline-offset-4">{t('writeArticle')}</Link>
                  {isAdmin && (
                    <Link to="/admin" className="block py-2.5 text-sm hover:underline underline-offset-4">{t('admin')}</Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMenuOpen(false); }}
                    className="block w-full text-left py-2.5 text-sm hover:underline underline-offset-4"
                  >
                    {t('logout')}
                  </button>
                </div>
              ) : (
                <Link to="/login" className="btn-outline !px-2 text-center">{t('login')}</Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
